import {
  ProductInclusionType,
  RebateRule,
  ThresholdType,
} from '@erp/shared-types';
import { useQueryClient } from '@tanstack/react-query';
import useGetApi from 'src/api/useGetApi';
import useMutationApi from 'src/api/useMutationApi';

export interface CreateRebateRuleRequest {
  rebateAgreementId: string;
  name: string;
  isRetroactiveToDollarOne: boolean | null; // null for manual rules
  isManual: boolean;
  thresholdType: ThresholdType | null; // null for manual rules
  growthBaseline?: string;
  discountPercentage?: number; // 0-100, optional
  notes?: string; // Optional notes about the rebate rule
  sortOrder?: number; // Optional, will default to next available order
  qualifyingInclusionType: ProductInclusionType | null; // null for manual rules
  affectedInclusionType: ProductInclusionType | null; // null for manual rules
  qualifyingProductGroups: { id: string }[];
  affectedProductGroups: { id: string }[];
  tiers: {
    tierOrder: number;
    thresholdValue: string;
    rebateValue: string;
  }[];
  divisions: string[];
}

export interface UpdateRebateRuleRequest {
  name?: string;
  isRetroactiveToDollarOne?: boolean | null; // null for manual rules
  isManual: boolean;
  thresholdType?: ThresholdType | null; // null for manual rules
  growthBaseline?: string;
  discountPercentage?: number; // 0-100, optional
  notes?: string; // Optional notes about the rebate rule
  sortOrder?: number; // Optional, for reordering
  qualifyingInclusionType?: ProductInclusionType | null; // null for manual rules
  affectedInclusionType?: ProductInclusionType | null; // null for manual rules
  qualifyingProductGroups?: { id: string }[];
  affectedProductGroups?: { id: string }[];
  tiers?: {
    id?: string;
    tierOrder: number;
    thresholdValue: string;
    rebateValue: string;
  }[];
  divisions?: string[];
}

// Get all rebate rules for an agreement
export const useRebateRulesByAgreement = (
  agreementId: string,
  enabled = true,
) => {
  return useGetApi<RebateRule[]>({
    endpoint: `rebates/rules/agreement/${agreementId}`,
    options: {
      enabled: enabled && !!agreementId,
    },
  });
};

// Create a rebate rule
export const useCreateRebateRule = () => {
  return useMutationApi<CreateRebateRuleRequest, RebateRule>({
    endpoint: 'rebates/rules',
    method: 'POST',
    successMessage: 'Rebate rule created successfully',
  });
};

// Update a rebate rule
export const useUpdateRebateRule = (id: string) => {
  return useMutationApi<UpdateRebateRuleRequest, RebateRule>({
    endpoint: `rebates/rules/${id}`,
    method: 'PUT',
    successMessage: 'Rebate rule updated successfully',
  });
};

// Delete a rebate rule
export const useDeleteRebateRule = (id: string) => {
  return useMutationApi<Record<string, never>>({
    endpoint: `rebates/rules/${id}`,
    method: 'DELETE',
    successMessage: 'Rebate rule deleted successfully',
  });
};

// Reorder rebate rules
export interface ReorderRebateRulesRequest {
  ruleOrders: {
    id: string;
    sortOrder: number;
  }[];
}

export const useReorderRebateRules = (agreementId: string) => {
  const queryClient = useQueryClient();

  return useMutationApi<ReorderRebateRulesRequest, RebateRule[]>({
    endpoint: `rebates/rules/agreement/${agreementId}/reorder`,
    method: 'PUT',
    successMessage: 'Rules reordered successfully',
    options: {
      onSuccess: () => {
        // Invalidate rebate rules query so edit mode gets fresh order
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            typeof query.queryKey[0] === 'string' &&
            query.queryKey[0].includes(
              `rebates/rules/agreement/${agreementId}`,
            ),
        });
      },
    },
  });
};

export default useRebateRulesByAgreement;
